import {
  Box,
  Container,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  type TPaymentOption,
  updateEarthquakeCoverage,
  updatePaymentPlan,
  useExcludeWcRuleResult,
  useSubmission,
  useSubmissionPrice,
} from 'api/submission';
import {
  BINARY_CONSTANTS,
  COVERAGE_CALLOUTS,
  isPurchaseActionDisabled,
  META,
  POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT,
  PURCHASE_ACTION_DISABLED_TOOLTIP_MESSAGE,
  PURCHASE_BLOCKED_MISSING_DOCUMENTS_MESSAGE,
  SCHEDULED_BUILDING_OCCUPANCY_VALUES,
  STATES,
  type TCoverageTableWCMessageVariants,
  WC_BLACKLIST_STATES,
  wcExcludedIndustryNames,
} from 'common/constants';
import type { TCoverageDetailsContentKeys } from 'components/CoverageDetails/CoverageDetailsContent/CoverageDetailsContent.types';
import { CoverageTable } from 'components/CoverageTable';
import type {
  CoverageCalloutAdditionalData,
  CoverageTableData,
  CoverageTableDataRow,
} from 'components/CoverageTable/CoverageTable.types';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { StatusToggle } from 'components/lib/StatusToggle';
import { QuoteDetailsCard } from 'components/QuoteDetailsCard';
import {
  currencyFormat,
  formatLocation,
  isGaragekeepersLimitExtractedFromLocationForVersion,
  isWCIncluded,
} from 'helpers/utils';
import { capitalize, isEmpty, omit } from 'lodash-es';
import theme from 'providers/theme/theme';
import { type CSSProperties, type FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';
import { useAppSelector } from 'store/hooks';
import { RatingStars } from './components/RatingStars';
import {
  QuoteExtraInfoStackStyle,
  QuoteFeedbackInfoStackStyle,
  QuoteFeedbackInfoWrapperStackStyle,
  QuoteMainStackStyle,
  QuoteStatusToggleSelectedStyle,
  QuoteStatusToggleStyle,
} from './Quote.styles';

const rating = 5;
const PRIMARY_ACTION_BTN_TEXT = 'Finalize & Purchase';

// eslint-disable-next-line complexity
const Quote: FC = () => {
  const { id } = useParams() as { id: string };
  const {
    data: submission,
    isLoading: isSubmissionLoading,
    isLoadingError,
    refetch: refetchSubmission,
  } = useSubmission(id);

  const { data: excludeWcRuleResult } = useExcludeWcRuleResult(id);
  const isWCExcludedByRule = excludeWcRuleResult?.has_exclude_wc_rule;
  const policyTaxKeyword = submission?.data?.policy_data?.pol_tax_keyword ?? '';

  const navigate = useNavigate();

  const isViewOnly =
    submission?.status === 'accepted' ||
    submission?.status === 'signature_pending' ||
    submission?.status === 'documents_pending';

  const isAnnualPaymentPlan = submission?.data?.policy_data?.pol_pay_plan === 'Annual';

  const isTableLayout = useMediaQuery(theme.breakpoints.down('md'));
  const {
    data: submissionPrice,
    isLoading: isSubmissionPriceLoading,
    refetch,
    isRefetching,
  } = useSubmissionPrice(
    id,
    {
      include_discounted_price: isAnnualPaymentPlan,
    },
    { enabled: !!submission },
  );

  const primaryLocation = formatLocation(
    omit(
      submission?.data.exposures.location.at(0)?.data?.loc_address_destructured,
      'latitude',
      'longitude',
    ),
  ).showing;

  const primaryLocationAddressString = primaryLocation.head + primaryLocation.tail;

  const quoteId = submission?.quote_number || '';

  const policyTerms = submission?.policyTerms || '';

  const priceLoading = isSubmissionPriceLoading || isRefetching;

  const businessName = submission?.data.uw_questions.businessName;

  const monthlyPayment = submissionPrice?.monthly_due ? +submissionPrice?.monthly_due : 0;

  const totalPayment = submissionPrice?.total_due ? +submissionPrice?.total_due : 0;

  const discountedTotalDue = submissionPrice?.discounted_total_due
    ? +submissionPrice?.discounted_total_due
    : 0;

  const EQsavingAmount = currencyFormat(submissionPrice?.earthquake_due || 0, 'USD', true).merged;

  const isBusinessRequiredDocumentsMissing = !!submission?.are_business_required_documents_missing;

  const finalizeAndPurchaseButtonDisabledMessage = isBusinessRequiredDocumentsMissing
    ? PURCHASE_BLOCKED_MISSING_DOCUMENTS_MESSAGE
    : PURCHASE_ACTION_DISABLED_TOOLTIP_MESSAGE;

  // eslint-disable-next-line complexity
  const tablesData = useMemo(() => {
    if (!submission) return [];

    const lastEmployer = submission.data.exposures.employer.at(-1);

    const manualPremiumPeril = lastEmployer?.perils?.find(
      (peril) => peril.data.man_ismain === 'Yes' && peril.name === 'manual_premium',
    );

    const coveragesCalloutsAdditionalData: CoverageCalloutAdditionalData = {
      'All Other Property': [],
      'Business Liability': [],
    };

    COVERAGE_CALLOUTS.forEach((callout) => {
      if (callout.industry) {
        if (callout.industry !== submission.data.policy_data.pol_tax_industry) return;
      }

      if (callout.subIndustry) {
        if (callout.subIndustry !== submission.data.policy_data.pol_tax_subindustry) return;
      }

      if (callout.additionalCriteria) {
        if (!callout.additionalCriteria(submission)) return;
      }

      coveragesCalloutsAdditionalData[callout.calloutParentCoverage].push({
        listKey: callout.triggerText,
        triggerText: callout.triggerText,
        detailsKey: callout.detailsKey,
      });
    });

    const primaryLocationExposure =
      submission.data.exposures.location.find(
        (location) => location.data.loc_address_isprimary === BINARY_CONSTANTS.YES,
      ) ?? submission.data.exposures.location.at(0);

    const garagekeepersPerilData = primaryLocationExposure?.perils?.find(
      (peril) => peril.name === 'garagekeepers_liability',
    )?.data;

    const isGaragekeepersVisible = garagekeepersPerilData?.gk_indicator === BINARY_CONSTANTS.YES;

    const garagekeepersLimit = isGaragekeepersLimitExtractedFromLocationForVersion(
      submission.three_config_version,
    )
      ? garagekeepersPerilData?.gk_ars_gk_limit_user || garagekeepersPerilData?.gk_ars_gk_limit
      : submission.data.policy_data.pol_ars_gk_limit_user ||
        submission.data.policy_data.pol_ars_gk_limit;

    const garagekeepersDeductible =
      submission.data.policy_data.pol_ars_gk_deductible_user ||
      submission.data.policy_data.pol_ars_gk_deductible;

    const coverageSections: CoverageTableData[] = [
      // Operations table section
      {
        title: 'Your Operations',
        subtitle: submission.primary_industry,
        additionalData: [
          {
            label: 'Annual Revenue',
            value: +(
              submission.data.uw_questions.grossRevenueUser ??
              submission.data.uw_questions.grossRevenue
            ),
          },
        ],
        rows: [
          {
            coverages: {
              triggerText: 'Business Liability',
              detailsKey: 'businessDetails',
            },
            additionalData: coveragesCalloutsAdditionalData['Business Liability'],
            limit: [
              {
                value: +submission.data.policy_data.pol_bl_limit_occurrence,
                label: 'per occurrence',
              },
              {
                value: +submission.data.policy_data.pol_bl_limit_aggregate,
                label: 'max',
              },
            ],
            deductible: [
              {
                value: +submission.data.policy_data.pol_bl_deductible || 'No deductible',
              },
            ],
            id: 'business_liability',
          },
          ...(isGaragekeepersVisible
            ? [
                {
                  coverages: {
                    triggerText: "Garagekeepers' Liability",
                    detailsKey: 'garagekeepersDetails' as TCoverageDetailsContentKeys,
                  },
                  limit: [{ value: +garagekeepersLimit }],
                  deductible: [{ value: +garagekeepersDeductible || 'No deductible' }],
                  id: 'garagekeepers_liability',
                },
              ]
            : []),
          ...(submission.data.exposures.vehicle.length > 0
            ? [
                {
                  id: 'auto_liability',
                  coverages: {
                    triggerText: 'Auto Liability',
                    detailsKey: 'autoDetails' as TCoverageDetailsContentKeys,
                  },
                  limit: [
                    {
                      value: +submission.data.policy_data.pol_al_limit,
                    },
                  ],
                  deductible: [
                    {
                      value: +submission.data.policy_data.pol_al_deductible || 'No deductible',
                    },
                  ],
                },
              ]
            : []),
          {
            coverages: {
              triggerText: 'Cyber Incident Response',
              detailsKey: 'cyberIncidentsDetails',
            },
            limit: [
              {
                value: +submission.data.policy_data.pol_cy_limit,
              },
            ],
            deductible: [
              {
                value: +submission.data.policy_data.pol_cy_deductible || 'No deductible',
              },
            ],
            id: 'cyber_incident_response',
          },
        ],
      },
    ];

    // Employees table section
    const { isWCIncluded: isWCIncludedValue, someOwnersOfficersIncluded } =
      isWCIncluded(submission);

    const isExcludedIndustry = wcExcludedIndustryNames.includes(policyTaxKeyword);

    let messageVariant: TCoverageTableWCMessageVariants = 'no_message';

    if (!someOwnersOfficersIncluded) {
      messageVariant = 'with_wc_ex_oo';
    }

    if (!isWCIncludedValue) {
      messageVariant = 'without_wc';
    }

    if (WC_BLACKLIST_STATES.includes(submission.us_state)) {
      messageVariant = 'wc_not_available';
    }

    if (isWCExcludedByRule && isExcludedIndustry) {
      messageVariant = 'wc_excluded_by_industry';
    }

    if (isWCExcludedByRule && !isExcludedIndustry) {
      messageVariant = 'wc_excluded_by_rule';
    }

    const workersCompRow: CoverageTableDataRow = {
      coverages: {
        triggerText: 'Worker`s Compensation',
        detailsKey: 'workersCompensationDetails',
      },
      limit: [
        {
          value: 'As required by law',
        },
      ],
      deductible: [
        {
          value: 'No deductible',
        },
      ],
      id: 'workers_compensation',
    };

    const workersCompMessageRow: CoverageTableDataRow = {
      id: 'workers_compensation_message',
      additionalValues: {
        messageVariant,
      },
    };

    coverageSections.push({
      title: 'Your Employees',
      additionalData: [
        {
          label: 'W2 Payroll',
          value: +manualPremiumPeril?.data?.man_payroll_w2 || '$0',
        },
        {
          label: '1099 Payroll',
          value: +manualPremiumPeril?.data?.man_payroll_sub || '$0',
        },
      ],
      rows: [
        ...(messageVariant === 'without_wc' ? [] : [workersCompRow]),
        ...(messageVariant === 'no_message' ? [] : [workersCompMessageRow]),
      ],
    });

    // Buildings table section
    if (submission.data.exposures.building.length > 0) {
      coverageSections.push({
        title: 'Your Property',
        subtitle: '',
        additionalData: [],
        rows: [
          ...(submission.data.exposures.building.reduce((acc, building) => {
            if (SCHEDULED_BUILDING_OCCUPANCY_VALUES.includes(building.data.bdg_occupancy))
              return [
                ...acc,
                {
                  coverages: {
                    listKey: building.id,
                    triggerText: `${building.data.bdg_name}, ${primaryLocationAddressString}`,
                    detailsKey: 'buildingDetails',
                  },
                  limit: [
                    {
                      value: +building.data.bdg_tiv_building,
                    },
                  ],
                  deductible: [
                    {
                      value: +building.data.bdg_deductible,
                    },
                  ],
                  id: building.data.bdg_name,
                },
              ];

            return acc;
          }, [] as CoverageTableDataRow[]) as CoverageTableDataRow[]),
          {
            coverages: {
              triggerText: 'All Other Property',
              detailsKey: 'allOtherPropertyDetails',
            },
            additionalData: coveragesCalloutsAdditionalData['All Other Property'],
            limit: [
              {
                value: +submission.data.policy_data.pol_op_limit || '$0',
              },
            ],
            deductible: [
              {
                value: +submission.data.policy_data.pol_op_deductible || 'No deductible',
              },
            ],
            id: 'all_other_property',
          },
          {
            coverages: {
              triggerText: 'Business Interruption due to Property Damage',
              detailsKey: 'businessInterruptionDueToPropertyDamageDetails',
            },
            limit: [
              {
                value: +submission.data.policy_data.pol_bi_limit || '$0',
                label: 'up to 1 year',
              },
            ],
            deductible: [
              {
                value: submission.data.policy_data.pol_bi_waiting_period
                  ? `${submission.data.policy_data.pol_bi_waiting_period} ${submission.data.policy_data.pol_bi_waiting_period > 1 ? 'days' : 'day'}`
                  : 'No deductible',
              },
            ],
            id: 'business_interruption',
          },
        ],
      });
    }

    // Vehicles table section
    if (submission.data.exposures.vehicle.length > 0) {
      coverageSections.push({
        title: 'Your Vehicles',
        subtitle: '',
        additionalData: [],
        rows: [
          ...submission.data.exposures.vehicle.map((vehicle) => ({
            coverages: {
              listKey: vehicle.id,
              triggerText: `${vehicle.data.veh_year} ${capitalize(vehicle.data.veh_make)} ${capitalize(vehicle.data.veh_model)} ${vehicle.data.veh_vin}`,
              detailsKey: 'vehicleDetails' as TCoverageDetailsContentKeys,
            },
            limit: [{ value: +vehicle.data.veh_stated_amt }],
            deductible: [{ value: +vehicle.data.veh_deductible }],
            id: vehicle.id,
          })),
          ...((submission.us_state === STATES.MI && submission.data.exposures.vehicle.length > 0
            ? [
                {
                  coverages: {
                    listKey: 'pol_pip_mi_total_medical_expenses',
                    triggerText: 'PIP Medical Expenses',
                    detailsKey: 'vehicleDetails' as TCoverageDetailsContentKeys,
                  },
                  limit: [
                    {
                      value:
                        submission.data.policy_data.pol_pip_mi_total_medical_expenses ===
                        POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT
                          ? 'Unlimited'
                          : +submission.data.policy_data.pol_pip_mi_total_medical_expenses,
                    },
                  ],
                  deductible: [{ value: '-' }],
                  id: 'pol_pip_mi_total_medical_expenses',
                },
                submission.data.policy_data.pol_pip_mi_total_medical_expenses !==
                POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT
                  ? {
                      coverages: {
                        listKey: 'pol_pip_mi_excess_attendant_care',
                        triggerText: 'Excess Attendant Care',
                        detailsKey: 'vehicleDetails' as TCoverageDetailsContentKeys,
                      },
                      limit: [
                        {
                          value:
                            submission.data.policy_data.pol_pip_mi_excess_attendant_care === '0'
                              ? '$0'
                              : +submission.data.policy_data.pol_pip_mi_excess_attendant_care,
                        },
                      ],
                      deductible: [{ value: '-' }],
                      id: 'pol_pip_mi_excess_attendant_care',
                    }
                  : false,
              ]
            : []
          ).filter(Boolean) as CoverageTableDataRow[]),
        ],
      });
    }

    return coverageSections;
  }, [submission, isSubmissionLoading, isLoadingError, isWCExcludedByRule]);

  const [extraCoverageIncluded, setExtraCoverageIncluded] = useState(false);
  const { setFullscreenLoading } = useFullscreenLoader();
  const showEarthQuakeCoverageSection = submission?.us_state === STATES.CA;

  const {
    actions: { setSection },
  } = useAppSelector('quoteStepper', ['actions']);

  useEffect(() => {
    if (!isSubmissionLoading && showEarthQuakeCoverageSection) {
      const isEarthquakeCoverageIncluded = submission?.data?.policy_data?.pol_eq_excluded === 'No';
      setExtraCoverageIncluded(isEarthquakeCoverageIncluded);
    }
  }, [isSubmissionLoading]);

  useEffect(() => {
    if (!isSubmissionPriceLoading) {
      refetchSubmission();
    }
  }, [isSubmissionPriceLoading]);

  useEffect(() => {
    if (isAnnualPaymentPlan) {
      refetch();
    }
  }, [isAnnualPaymentPlan]);

  const handleExtraCoverageIncluded = async () => {
    try {
      setFullscreenLoading(true, `Saving the changes...`);
      // extraCoverageIncluded didn't updated yet.
      // So, if the current value is true, it means it will be false/excluded
      const isExcluded = extraCoverageIncluded ? 'Yes' : 'No';
      await updateEarthquakeCoverage({ submissionId: id, pol_eq_excluded: isExcluded });
      refetch();
      setExtraCoverageIncluded((prev) => !prev);
    } catch (err) {
      displayBackendErrorMessage(err);
    } finally {
      setFullscreenLoading(false);
    }
  };

  // eslint-disable-next-line complexity
  const renderPurchaseScreen = useMemo(() => {
    const { uw_questions, policy_data } = submission?.data || {};

    // FEIN and SSN might appear empty due to lack of sync from the agent portal,
    // but if the submission has accepted status, those fields were definitely filled.
    // So `!isViewOnly` ensures we only show the section when the data truly hasn't been entered yet.
    const renderOperationsSectionOnPurchaseScreen =
      isEmpty(uw_questions?.fein) && isEmpty(uw_questions?.ssn) && !isViewOnly;

    const renderUnemploymentNumberQuestionOnPurchaseScreen =
      !!submission &&
      isWCIncluded(submission).isWCIncluded &&
      policy_data?.pol_state === STATES.MN &&
      uw_questions?.entityType !== 'SoleProprietor' &&
      uw_questions?.numEmployees > 0;

    const { building: buildings, vehicle: vehicles } = submission?.data?.exposures || {};
    const scheduledBuildings = buildings?.filter((building) =>
      SCHEDULED_BUILDING_OCCUPANCY_VALUES.includes(building.data.bdg_occupancy),
    );

    const renderPropertiesSectionOnPurchaseScreen = !isEmpty(scheduledBuildings);
    const renderVehiclesSectionOnPurchaseScreen = !isEmpty(vehicles);

    return (
      renderOperationsSectionOnPurchaseScreen ||
      renderUnemploymentNumberQuestionOnPurchaseScreen ||
      renderPropertiesSectionOnPurchaseScreen ||
      renderVehiclesSectionOnPurchaseScreen
    );
  }, [
    submission?.data?.uw_questions,
    submission?.data?.policy_data,
    submission?.data?.exposures,
    isViewOnly,
  ]);

  const handleFinalizeAndPurchase = async () => {
    if (!renderPurchaseScreen) {
      if (submission?.status === 'quoted') {
        try {
          setFullscreenLoading(true, 'Creating payment');

          await updatePaymentPlan({
            submissionId: id,
            body: {
              payment_schedule: submission?.data.paymentScheduleName as TPaymentOption,
            },
          });
        } catch (err) {
          displayBackendErrorMessage(err);

          await refetchSubmission();

          window?.scrollTo(0, 0);
          return;
        } finally {
          setFullscreenLoading(false);
        }
      }

      navigate(`/submission/${id}/payment`);
    } else {
      setSection(2);
    }
  };

  const isFinalizeAndPurchaseButtonDisabled =
    (isPurchaseActionDisabled && !renderPurchaseScreen && !isViewOnly) ||
    isBusinessRequiredDocumentsMissing;
  return (
    <>
      {isTableLayout && (
        <QuoteDetailsCard
          quoteId={quoteId}
          primaryAction={handleFinalizeAndPurchase}
          primaryActionBtnText={PRIMARY_ACTION_BTN_TEXT}
          contentType="paymentDetails"
          totalPayment={totalPayment}
          isAnnualPaymentPlan={isAnnualPaymentPlan}
          discountedTotalDue={discountedTotalDue}
          monthlyPayment={monthlyPayment}
          priceLoading={priceLoading}
          isPrimaryActionDisabled={isBusinessRequiredDocumentsMissing}
          primaryActionDisabledMessage={PURCHASE_BLOCKED_MISSING_DOCUMENTS_MESSAGE}
          mainContent={
            <Stack gap={2.5}>
              {showEarthQuakeCoverageSection && (
                <Stack gap={1}>
                  <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3">Earthquake Coverage</Typography>

                    <StatusToggle
                      selectedText="Included"
                      notSelectedText="Excluded"
                      selected={extraCoverageIncluded}
                      disabled={isViewOnly}
                      value=""
                      onChange={handleExtraCoverageIncluded}
                      selectedBgColor={theme.palette.customColors.copper as CSSProperties}
                      notSelectedBgColor={theme.palette.customColors.darkGray as CSSProperties}
                      sx={[
                        extraCoverageIncluded
                          ? QuoteStatusToggleSelectedStyle
                          : QuoteStatusToggleStyle,

                        priceLoading ? { pointerEvents: 'none' } : {},
                      ]}
                    />
                  </Stack>

                  <Typography variant="body1">
                    {isSubmissionPriceLoading ? (
                      <Skeleton width="100%" height="24px" />
                    ) : (
                      `Save ${EQsavingAmount} by excluding Earthquake Coverage.`
                    )}
                  </Typography>
                </Stack>
              )}

              <Stack gap={1}>
                <Typography variant="h3">Policy Term</Typography>

                <Typography>{policyTerms}</Typography>
              </Stack>
            </Stack>
          }
        />
      )}

      <Container maxWidth="lg">
        <Box mb={1.5} maxWidth={isTableLayout ? 'auto' : 722} textAlign="justify">
          <Typography variant="h3">Here&apos;s your business insurance quote for</Typography>

          <Typography variant="h1">{businessName}</Typography>

          <Typography variant="body2" mt={1.5}>
            Review the coverages, limits and deductibles that make part of THREE&apos;s
            comprehensive policy for your business.
          </Typography>
        </Box>

        <Stack sx={QuoteMainStackStyle}>
          <Box width={isTableLayout ? '100%' : '55%'} flexGrow={1}>
            {isSubmissionLoading ? (
              <Skeleton width="100%" height="400px" />
            ) : (
              <CoverageTable tablesData={tablesData} quoteId={quoteId} />
            )}
          </Box>

          <Stack gap={3} width={isTableLayout ? '100%' : '35%'}>
            {!isTableLayout && (
              <QuoteDetailsCard
                quoteId={quoteId}
                primaryAction={handleFinalizeAndPurchase}
                primaryActionBtnText={PRIMARY_ACTION_BTN_TEXT}
                totalPayment={totalPayment}
                discountedTotalDue={discountedTotalDue}
                isAnnualPaymentPlan={isAnnualPaymentPlan}
                contentType="paymentDetails"
                monthlyPayment={monthlyPayment}
                priceLoading={priceLoading}
                isPrimaryActionDisabled={isBusinessRequiredDocumentsMissing}
                primaryActionDisabledMessage={PURCHASE_BLOCKED_MISSING_DOCUMENTS_MESSAGE}
                mainContent={
                  <Stack gap={2.5}>
                    {showEarthQuakeCoverageSection && (
                      <Stack gap={1}>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="h3">Earthquake Coverage</Typography>

                          <StatusToggle
                            selectedText="Included"
                            notSelectedText="Excluded"
                            selected={extraCoverageIncluded}
                            disabled={isViewOnly}
                            value=""
                            onChange={handleExtraCoverageIncluded}
                            selectedBgColor={theme.palette.customColors.copper as CSSProperties}
                            notSelectedBgColor={
                              theme.palette.customColors.darkGray as CSSProperties
                            }
                            sx={[
                              extraCoverageIncluded
                                ? QuoteStatusToggleSelectedStyle
                                : QuoteStatusToggleStyle,

                              priceLoading ? { pointerEvents: 'none' } : {},
                            ]}
                          />
                        </Stack>

                        <Typography variant="body1">
                          {isSubmissionPriceLoading ? (
                            <Skeleton width="100%" height="24px" />
                          ) : (
                            `Save ${EQsavingAmount} by excluding Earthquake Coverage.`
                          )}
                        </Typography>
                      </Stack>
                    )}

                    <Stack gap={1}>
                      <Typography variant="h3">Policy Term</Typography>

                      <Typography>{policyTerms}</Typography>
                    </Stack>

                    <Stack gap={1.25}>
                      <Tooltip
                        title={
                          isFinalizeAndPurchaseButtonDisabled
                            ? finalizeAndPurchaseButtonDisabledMessage
                            : ''
                        }
                      >
                        <Box>
                          <PrimaryButton
                            text={PRIMARY_ACTION_BTN_TEXT}
                            data-test="finalizeAndPurchase"
                            disabled={isFinalizeAndPurchaseButtonDisabled}
                            fullWidth
                            onClick={handleFinalizeAndPurchase}
                            sx={
                              priceLoading ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}
                            }
                          />
                        </Box>
                      </Tooltip>

                      {/* <SecondaryButton
                        text="Edit answers"
                        startAdornment={<SvgIconWrapper src={penIcon} />}
                        onClick={handleEditAnswers}
                        sx={SecondaryButtonStyle}
                      /> */}
                    </Stack>
                  </Stack>
                }
              />
            )}

            {/* {isTableLayout && (
              <SecondaryButton
                text="Edit answers"
                startAdornment={<SvgIconWrapper src={penIcon} />}
                onClick={handleEditAnswers}
              />
            )} */}

            <Stack gap={1} sx={QuoteExtraInfoStackStyle}>
              <Typography variant="h2">Why choose THREE?</Typography>

              <Stack gap={1.5}>
                <Typography variant="body1">
                  <Box component="span" fontWeight={800} mr={0.5}>
                    Get all the critical coverages
                  </Box>
                  your business needs in a single comprehensive policy.
                </Typography>

                <Typography variant="body1">
                  <Box component="span" fontWeight={800} mr={0.5}>
                    Our{' '}
                    <Link href={META.THE_POLICY_LINK} target="_blank">
                      3 page policy
                    </Link>{' '}
                    removes
                  </Box>
                  the fine print, so there are no surprises.
                </Typography>

                <Typography variant="body1">
                  <Box component="span" fontWeight={800} mr={0.5}>
                    Coverage backed
                  </Box>
                  by the financial strength and reputation of Berkshire Hathaway.
                </Typography>
              </Stack>
            </Stack>

            <Stack sx={QuoteFeedbackInfoWrapperStackStyle}>
              <Typography variant="h2">
                See what other business owners are saying about THREE
              </Typography>

              <Stack gap={1.5} sx={QuoteFeedbackInfoStackStyle}>
                <Typography variant="h3">Quick and Easy</Typography>

                <Typography variant="body1">
                  Took only a few minutes to get my insurance. Agent was great and friendly. Had
                  policy emailed to me in minutes
                </Typography>

                <RatingStars rating={rating} />

                <Typography variant="body1" color={theme.palette.customColors.darkGray}>
                  James S.
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </>
  );
};

export default Quote;
